import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Autocomplete } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getAddons, createAddon, updateAddon, deleteAddon } from '../../api/resourceApi';
import { getLogsByModules } from '../../api/logApi';
import { useUser } from '../../context/userContext';
import useTableState from '../../hooks/useTableState';
import { ADDON_UNIT_TYPES, unitSuffix, unitWord, unitLabel } from '../../utils/addonUnits';
import { facetFilterOptions } from '../common/facetFilter';

const PP = "'Poppins', sans-serif";

// Format price for display: "RM 800 /panel" when unit type set, otherwise raw
// price string. The "base+rate" syntax (e.g. "200+50") renders as
// "RM 200 + RM 50 /item" so the catalog table makes the formula obvious at a glance.
const formatPriceDisplay = (item) => {
    if (!item.unitType) return item.price || '';
    const rate = unitSuffix(item.unitType);
    const raw = String(item.price ?? '');
    if (raw.includes('+')) {
        const cleaned = raw.replace(/[^\d.+]/g, '');
        const [b, p] = cleaned.split('+');
        const base = parseFloat(b);
        const per = parseFloat(p);
        if (Number.isFinite(base) && Number.isFinite(per)) {
            return `RM ${base.toLocaleString()} + RM ${per.toLocaleString()} ${rate}`;
        }
    }
    const num = parseFloat(raw.replace(/[^\d.]/g, ''));
    if (!Number.isFinite(num)) return `${item.price} ${rate}`;
    return `RM ${num.toLocaleString()} ${rate}`;
};

const PHASE_OPTIONS = ['Any', 'Single Phase', 'Three Phase'];
const SEGMENT_OPTIONS = ['Any', 'Residential', 'Commercial'];

export default function AddonsTab({ isAdmin: isAdminProp } = {}) {
    const { user } = useUser();
    const isAdmin = isAdminProp ?? user?.roles?.some(r => ['Admin', 'Super Admin'].includes(r));
    const ts = useTableState('addons', { defaultSorting: [{ id: 'defaultSlot', desc: false }] });
    const [data, setData] = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);

    const fetchData = useCallback(async () => {
        try { const res = await getAddons(); setData(res.data || []); } catch (e) { console.error(e); }
    }, []);
    const fetchLastUpdate = useCallback(async () => {
        try { const res = await getLogsByModules('Addon', 1, 1); if (res.data?.data?.length > 0) setLastUpdate(res.data.data[0]); } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => { fetchData(); fetchLastUpdate(); }, [fetchData, fetchLastUpdate]);

    const categories = useMemo(() => [...new Set(data.map(d => d.category || 'General'))], [data]);

    const handleSave = async (addAnother = false) => {
        if (!editItem?.itemName) return;
        try {
            if (editItem.addonId) {
                await updateAddon(editItem);
                setDialogOpen(false);
                setEditItem(null);
            } else {
                await createAddon(editItem);
                if (addAnother) {
                    setEditItem(prev => ({ ...prev, addonId: undefined, itemName: '', price: '' }));
                } else {
                    setDialogOpen(false);
                    setEditItem(null);
                }
            }
            fetchData(); fetchLastUpdate();
        } catch (e) { alert('Failed to save'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this item?')) return;
        await deleteAddon(id);
        fetchData(); fetchLastUpdate();
    };

    const openAdd = () => {
        setEditItem({ itemName: '', price: '', category: categories[0] || '', sortOrder: 0, phase: 'Any', segment: 'Any' });
        setDialogOpen(true);
    };

    const columns = useMemo(() => [
        {
            // The REAL AddonId, not a row position. It used to render row.index + 1, which
            // shifted with sorting/filtering and made it impossible to say which record you
            // were looking at — two add-ons with the same name showed the same "#".
            // Now it is the database id, so it is sortable and the global search finds it.
            // (user 2026-08-16)
            id: 'addonId', header: '#', accessorKey: 'addonId', size: 60, enableColumnFilter: false,
            Cell: ({ cell }) => (
                <span style={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.78rem' }}>
                    #{cell.getValue()}
                </span>
            ),
        },
        {
            id: 'defaultSlot', header: 'Slot', accessorKey: 'defaultSlot', size: 70,
            Cell: ({ cell }) => {
                const v = cell.getValue();
                if (v == null) return <span style={{ color: '#CBD5E1' }}>—</span>;
                return (
                    <span style={{ display: 'inline-block', minWidth: 22, padding: '1px 6px', borderRadius: 6, backgroundColor: '#EFF6FF', color: '#1A8CFF', fontWeight: 700, fontSize: '0.72rem', textAlign: 'center' }}>
                        #{v}
                    </span>
                );
            },
        },
        { id: 'itemName', header: 'Item', accessorKey: 'itemName', size: 240 },
        {
            id: 'price', header: 'Price / Rate', size: 140,
            accessorFn: (row) => {
                // Numeric accessor so sort works; display uses formatPriceDisplay.
                const num = parseFloat(String(row.price ?? '').replace(/[^\d.]/g, ''));
                return Number.isFinite(num) ? num : 0;
            },
            Cell: ({ row }) => (
                <span style={{ fontWeight: 600, color: '#10B981' }}>{formatPriceDisplay(row.original)}</span>
            ),
        },
        { id: 'category', header: 'Category', accessorKey: 'category', size: 150 },
        {
            id: 'description', header: 'Description', accessorKey: 'description', size: 320,
            Cell: ({ cell }) => {
                const raw = cell.getValue();
                if (!raw) return <span style={{ color: '#CBD5E1' }}>—</span>;
                const lines = String(raw).split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                return (
                    <Box sx={{ color: '#64748B', fontSize: '0.78rem', lineHeight: 1.45 }}>
                        {lines.map((s, i) => (
                            <div key={i}>{s.startsWith('-') ? s : `- ${s}`}</div>
                        ))}
                    </Box>
                );
            },
        },
        { id: 'phase', header: 'Phase', accessorKey: 'phase', size: 110, Cell: ({ cell }) => cell.getValue() || 'Any' },
        { id: 'segment', header: 'Segment', accessorKey: 'segment', size: 120, Cell: ({ cell }) => cell.getValue() || 'Any' },
        {
            id: 'unitType', header: 'Unit', accessorKey: 'unitType', size: 90,
            Cell: ({ cell }) => {
                return unitLabel(cell.getValue());
            },
        },
        ...(isAdmin ? [{
            id: 'actions', header: '', size: 120, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                    <span onClick={() => { setEditItem({ ...row.original }); setDialogOpen(true); }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: PP }}>
                        Edit
                    </span>
                    <span onClick={() => handleDelete(row.original.addonId)}
                        style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: PP }}>
                        Delete
                    </span>
                </Box>
            ),
        }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [isAdmin]);

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns,
        data,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showColumnFilters: false, showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px', verticalAlign: 'top' } },
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
    });

    return (
        <Box>
            {lastUpdate && (
                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mb: 1 }}>
                    Last updated: {new Date(lastUpdate.createdOn).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })} by {lastUpdate.createdBy} — {lastUpdate.action}: {lastUpdate.remarks}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                {isAdmin && (
                    <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openAdd}
                        sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                        Add Item
                    </Button>
                )}
            </Box>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
            </div>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>{editItem?.addonId ? 'Edit Item' : 'Add Item'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
                    <Autocomplete
                        freeSolo
                        options={categories}
                        value={editItem?.category || ''}
                        onInputChange={(_, v) => setEditItem(p => ({ ...p, category: v || '' }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Category" size="small"
                                helperText="Pick from existing or type a new category"
                            />
                        )}
                    />
                    <TextField label="Title (Item Name)" value={editItem?.itemName || ''} onChange={e => setEditItem(p => ({ ...p, itemName: e.target.value }))} size="small"
                        placeholder="e.g. Upgrade to Auto Backup-Box" />
                    <TextField label="Description" value={editItem?.description || ''} onChange={e => setEditItem(p => ({ ...p, description: e.target.value }))} size="small"
                        multiline minRows={2}
                        placeholder={"e.g.\nContactor, signal cable\nRelay, time relay"}
                        helperText="Each new line becomes its own bullet on the quotation (e.g. press Enter for a 2nd line → 2 bullets)." />
                    <FormControl size="small">
                        <InputLabel>Phase</InputLabel>
                        <Select value={editItem?.phase || 'Any'} onChange={e => setEditItem(p => ({ ...p, phase: e.target.value }))} label="Phase">
                            {PHASE_OPTIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small">
                        <InputLabel>Segment</InputLabel>
                        <Select value={editItem?.segment || 'Any'} onChange={e => setEditItem(p => ({ ...p, segment: e.target.value }))} label="Segment">
                            {SEGMENT_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small">
                        <InputLabel>Unit Type</InputLabel>
                        <Select value={editItem?.unitType || ''} onChange={e => setEditItem(p => ({ ...p, unitType: e.target.value }))} label="Unit Type">
                            {ADDON_UNIT_TYPES.map(t => (
                                <MenuItem key={t.value || 'none'} value={t.value}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small">
                        <InputLabel>Default Slot (Quotation auto-include)</InputLabel>
                        <Select
                            value={editItem?.defaultSlot ?? ''}
                            onChange={e => setEditItem(p => ({ ...p, defaultSlot: e.target.value === '' ? null : Number(e.target.value) }))}
                            label="Default Slot (Quotation auto-include)">
                            <MenuItem value="">— None (picker-only) —</MenuItem>
                            <MenuItem value={1}>Slot 1</MenuItem>
                            <MenuItem value={2}>Slot 2</MenuItem>
                            <MenuItem value={3}>Slot 3</MenuItem>
                            <MenuItem value={4}>Slot 4</MenuItem>
                            <MenuItem value={5}>Slot 5</MenuItem>
                        </Select>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP, mt: 0.5 }}>
                            Items in Slots 1–5 auto-include as the 5 starter rows in every new quotation (phase-filtered).
                        </Typography>
                    </FormControl>
                    {editItem?.unitType ? (() => {
                        // Two separate fields — admins fill what they know. Combined into
                        // a single `price` string on the way out so the existing API/DB
                        // contract stays the same:
                        //   base=0,  rate=170      → "170"        (flat per unit × qty)
                        //   base=700,rate=170      → "700+170"    (RM 700 setup + RM 170/unit × qty)
                        //   base=200,rate=0        → "200"        (one-off RM 200; treated as flat-1)
                        // Sales side already parses both shapes — no breaking changes.
                        const raw = String(editItem?.price ?? '');
                        const cleaned = raw.replace(/[^\d.+]/g, '');
                        let baseStr = '', rateStr = '';
                        if (cleaned.includes('+')) {
                            const parts = cleaned.split('+');
                            baseStr = parts[0] || '';
                            rateStr = parts[1] || '';
                        } else {
                            rateStr = cleaned;
                        }
                        const setCombined = (newBase, newRate) => {
                            const b = (newBase || '').toString().replace(/[^\d.]/g, '');
                            const r = (newRate || '').toString().replace(/[^\d.]/g, '');
                            const combined = (parseFloat(b) > 0) ? (r === '' ? b : `${b}+${r}`) : r;
                            setEditItem(p => ({ ...p, price: combined }));
                        };
                        const unitWordLbl = unitWord(editItem.unitType);
                        const baseNum = parseFloat(baseStr) || 0;
                        const rateNum = parseFloat(rateStr) || 0;
                        const previewQty = 10;
                        const previewTotal = baseNum + rateNum * previewQty;
                        return (
                            <>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0F172A', fontFamily: PP, mt: 0.5 }}>
                                    Pricing
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <TextField label="Base Price (RM)" type="number" size="small" fullWidth
                                        value={baseStr}
                                        onChange={e => setCombined(e.target.value, rateStr)}
                                        placeholder="0"
                                        helperText="One-off setup fee (optional). Leave 0 if none." />
                                    <TextField label={`Rate (RM) — per ${unitWordLbl}`} type="number" size="small" fullWidth
                                        value={rateStr}
                                        onChange={e => setCombined(baseStr, e.target.value)}
                                        placeholder="e.g. 170"
                                        helperText={`Multiplied by qty in the calculator.`} />
                                </Box>
                                <Typography sx={{ fontSize: '0.72rem', color: '#0F62CC', fontFamily: PP, lineHeight: 1.5, bgcolor: '#F0F7FF', border: '1px solid #DBEAFE', borderRadius: 1, p: 1 }}>
                                    <strong>Formula:</strong> Total = Base + Rate × qty<br />
                                    <strong>Example</strong> at qty {previewQty} {unitWordLbl}{previewQty === 1 ? '' : 's'}: RM {baseNum.toLocaleString()} + {previewQty} × RM {rateNum.toLocaleString()} = <strong>RM {previewTotal.toLocaleString()}</strong>
                                </Typography>
                            </>
                        );
                    })() : (
                        <TextField label="Price / Rate" value={editItem?.price || ''} onChange={e => setEditItem(p => ({ ...p, price: e.target.value }))} size="small"
                            placeholder="e.g. RM500 per visit, RM800 – RM1,500" />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    {!editItem?.addonId && (
                        <Button onClick={() => handleSave(true)} variant="outlined" sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#1A8CFF', color: '#1A8CFF' }}>Save & Add Another</Button>
                    )}
                    <Button onClick={() => handleSave(false)} variant="contained" sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
